import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  CopyIcon,
  DotsHorizontalIcon,
  LockClosedIcon,
  LockOpen1Icon,
  Pencil1Icon,
  TrashIcon,
} from '@radix-ui/react-icons'
import { putFetcher } from 'api/swr'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { color2Linear } from 'CanvasCore/utils'
import { ColorCard } from 'components/ui/ColorCard'
import { useSelectedColor, useUI } from 'Editor'
import {
  useMemo,
  type ComponentType,
  type FC,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react'

import { useParams } from 'react-router-dom'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import type { ColorSet } from './types'

const TypedColorCard = ColorCard as ComponentType<
  HTMLAttributes<HTMLDivElement> & { children?: ReactNode }
>

interface ColorSwatchProps {
  color: ColorSet
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  isDeleting?: boolean
}

export const ColorSwatch: FC<ColorSwatchProps> = ({
  color,
  onEdit,
  onDuplicate,
  onDelete,
  isDeleting,
}) => {
  const { tid } = useParams<{ tid: string }>()
  const { id, colors, requireAd } = color
  const linear = useMemo(() => color2Linear(colors), [colors])
  const toningId = useUI((state) => state.toningId)
  const requiresAd = requireAd === 1

  const { trigger: updateRequireAd, isMutating: isUpdatingRequireAd } =
    useSWRMutation(`/tonings/${toningId}/colors/require-ad`, putFetcher)

  const handleToggleRequireAd = async () => {
    const nextRequireAd = requiresAd ? 0 : 1
    await updateRequireAd({ tid, colorId: id, requireAd: nextRequireAd })
    mutate(['/tonings', { tid }])
    const enabled = nextRequireAd === 1
    toast({
      title: enabled ? 'Ad unlock enabled' : 'Ad requirement removed',
      description: enabled
        ? 'Users must watch an ad to unlock this color.'
        : 'This color no longer requires watching an ad.',
      className: 'bg-green-500 text-white',
    })
  }
  const selectedColor = useSelectedColor(tid!, 'default')
  const selected = selectedColor?.id === id

  const selectColor = useEditorStore((state) => state.selectColor)
  const deselectColor = useEditorStore((state) => state.deselectColor)

  const handleSelect = () => {
    if (selected) {
      deselectColor(tid!, toningId!)
    } else {
      selectColor(tid!, toningId!, 'default', { id, color: linear })
    }
  }

  const stopPropagation = (e: MouseEvent) => {
    e.stopPropagation()
  }

  if (!color || !linear) return null

  return (
    <TypedColorCard
      onClick={handleSelect}
      className={cn(
        'relative block p-0 overflow-hidden bg-transparent border-transparent',
        selected && 'border-2 border-primary ring-1 ring-primary/30',
      )}
    >
      <div
        className="absolute inset-0 rounded-[inherit]"
        style={{ backgroundImage: linear }}
      />
      {requiresAd && (
        <span className="absolute bottom-2 left-2 z-10 rounded px-2 py-1 text-[14px] font-semibold leading-none bg-green-500/60 text-white">
          AD
        </span>
      )}
      <div
        className="absolute top-1 right-1 z-10"
        onClick={stopPropagation}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="icon"
              className="h-7 w-7 rounded-md border border-black/10 bg-white/95 p-0 text-foreground shadow-sm hover:bg-white data-[state=open]:bg-white"
            >
              <DotsHorizontalIcon className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={onEdit}>
              <Pencil1Icon className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <CopyIcon className="mr-2 h-4 w-4" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(id)}
            >
              <CopyIcon className="mr-2 h-4 w-4" />
              Copy color id
            </DropdownMenuItem>
            {/* 染色设置ad标识功能隐藏 */}
            {/* <DropdownMenuItem
              onClick={handleToggleRequireAd}
              disabled={isUpdatingRequireAd}
            >
              {requiresAd ? (
                <>
                  <LockOpen1Icon className="mr-2 h-4 w-4" />
                  Remove ad
                </>
              ) : (
                <>
                  <LockClosedIcon className="mr-2 h-4 w-4" />
                  Mark ad
                </>
              )}
            </DropdownMenuItem> */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
              onSelect={onDelete}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete {isDeleting ? '...' : null}
              <DropdownMenuShortcut>⌘⌫</DropdownMenuShortcut>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TypedColorCard>
  )
}
